package songwritersnotebook.app.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import songwritersnotebook.app.utils.PreferencesManager
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.Dispatchers

import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.SharingStarted
import songwritersnotebook.app.di.PlatformDependencyProvider

import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp

enum class TextSize(val displayName: String, val fontSize: TextUnit) {
    Small("Small", 14.sp),
    Medium("Medium", 16.sp),
    Large("Large", 20.sp)
}

data class UserSettings(
    val textSize: TextSize = TextSize.Medium,
    val isDarkMode: Boolean = true,
    val downloadAllAudio: Boolean = true,
    val dailyReminderEnabled: Boolean = true,
    val newsAndUpdatesEnabled: Boolean = true,
    val isLeftHanded: Boolean = false
)

class SettingsViewModel : ViewModel() {
    private val preferencesManager = PreferencesManager()
    private val songRepository = PlatformDependencyProvider().getSongRepository()

    private val _settings = MutableStateFlow(UserSettings(
        textSize = try { TextSize.valueOf(preferencesManager.textSize) } catch(e: Exception) { TextSize.Medium },
        downloadAllAudio = preferencesManager.downloadAllAudio,
        dailyReminderEnabled = preferencesManager.dailyReminderEnabled,
        newsAndUpdatesEnabled = preferencesManager.newsAndUpdatesEnabled,
        isLeftHanded = preferencesManager.isLeftHanded,
        isDarkMode = preferencesManager.isDarkMode
    ))
    val settings: StateFlow<UserSettings> = _settings.asStateFlow()

    // Expose flows for simple boolean toggles to match source if needed, or stick to StateFlow.
    // Source used State<Boolean>. Here we use StateFlow. Ui collects it.
    
    val isLeftHanded: StateFlow<Boolean> = _settings.map { it.isLeftHanded }.stateIn(viewModelScope, SharingStarted.Eagerly, preferencesManager.isLeftHanded)
    val isDarkMode: StateFlow<Boolean> = _settings.map { it.isDarkMode }.stateIn(viewModelScope, SharingStarted.Eagerly, preferencesManager.isDarkMode)
    val downloadAllAudio: StateFlow<Boolean> = _settings.map { it.downloadAllAudio }.stateIn(viewModelScope, SharingStarted.Eagerly, preferencesManager.downloadAllAudio)
    val dailyReminderEnabled: StateFlow<Boolean> = _settings.map { it.dailyReminderEnabled }.stateIn(viewModelScope, SharingStarted.Eagerly, preferencesManager.dailyReminderEnabled)
    val newsAndUpdatesEnabled: StateFlow<Boolean> = _settings.map { it.newsAndUpdatesEnabled }.stateIn(viewModelScope, SharingStarted.Eagerly, preferencesManager.newsAndUpdatesEnabled)
    
    // For TextSize, we need StateFlow<TextSize> or just use settings.value.textSize
    val textSize: StateFlow<TextSize> = _settings.map { it.textSize }.stateIn(viewModelScope, SharingStarted.Eagerly, TextSize.Medium)

    val isRepairing = MutableStateFlow(false)
    val repairProgress = MutableStateFlow("")

    fun cycleTextSize() {
        val current = _settings.value.textSize
        val next = when(current) {
            TextSize.Small -> TextSize.Medium
            TextSize.Medium -> TextSize.Large
            TextSize.Large -> TextSize.Small
        }
        _settings.value = _settings.value.copy(textSize = next)
        preferencesManager.textSize = next.name
    }

    fun toggleDarkMode() {
        val newValue = !_settings.value.isDarkMode
        _settings.value = _settings.value.copy(isDarkMode = newValue)
        preferencesManager.isDarkMode = newValue
    }

    fun toggleLeftHandedMode() {
        val newValue = !_settings.value.isLeftHanded
        _settings.value = _settings.value.copy(isLeftHanded = newValue)
        preferencesManager.isLeftHanded = newValue
    }

    fun toggleDownloadAllAudio() {
        val newValue = !_settings.value.downloadAllAudio
        _settings.value = _settings.value.copy(downloadAllAudio = newValue)
        preferencesManager.downloadAllAudio = newValue
        
        if (newValue) {
            viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
                songRepository?.syncAllRecordings { }
            }
        }
    }

    fun toggleDailyReminder() {
        val newValue = !_settings.value.dailyReminderEnabled
        _settings.value = _settings.value.copy(dailyReminderEnabled = newValue)
        preferencesManager.dailyReminderEnabled = newValue
    }

    fun toggleNewsAndUpdates() {
        val newValue = !_settings.value.newsAndUpdatesEnabled
        _settings.value = _settings.value.copy(newsAndUpdatesEnabled = newValue)
        preferencesManager.newsAndUpdatesEnabled = newValue
        songwritersnotebook.app.utils.NotificationManager().setNewsAndUpdatesSubscription(newValue)
    }

    fun clearAudioCache() {
        viewModelScope.launch {
            songRepository?.clearAllLocalAudio { count ->
                songwritersnotebook.app.utils.PlatformToast.show("Cleared $count local audio files.")
            }
        }
    }

    fun resyncAllData(mainViewModel: MainViewModel) {
        viewModelScope.launch {
            println("DEBUG: SettingsViewModel - Starting Resync...")
            mainViewModel.updateIsSyncing(true)
            songRepository?.syncSongsWithFirestore {
                viewModelScope.launch {
                    songRepository.uploadMissingRecordings() // New
                    songRepository.syncAllRecordings { progress ->
                        if (progress.isBlank()) {
                            mainViewModel.updateIsSyncing(false)
                        }
                    }
                }
            }
        }
    }

    fun repairInstrumentCache() {
        if (isRepairing.value) return
        viewModelScope.launch {
            isRepairing.value = true
            repairProgress.value = "Repairing..."
            
            val instruments = listOf("piano", "guitar", "bass", "drums")
            songwritersnotebook.app.utils.AudioSampleLoader.cacheAllSamples(instruments) { progress ->
                repairProgress.value = "Progress: ${(progress * 100).toInt()}%"
            }
            
            repairProgress.value = "Done"
            isRepairing.value = false
            songwritersnotebook.app.utils.PlatformToast.show("Instrument cache repaired!")
        }
    }

    fun scanForOrphans(onResult: (Int) -> Unit) {
        viewModelScope.launch {
            isRepairing.value = true
            repairProgress.value = "Scanning..."
            
            // Get all owned songs
            val allSongs = songRepository?.songs?.value ?: emptyList()
            var totalRecovered = 0
            
            for (song in allSongs) {
                // Only scan owned songs
                val currentUserId = songRepository?.getCurrentUserId()
                if (song.ownerId == currentUserId) {
                    repairProgress.value = "Scanning ${song.title}..."
                    val count = songRepository?.recoverOrphanedRecordings(song.id) ?: 0
                    totalRecovered += count
                }
            }
            
            repairProgress.value = "Done"
            isRepairing.value = false
            onResult(totalRecovered)
        }
    }

    suspend fun recalculateStorage(userId: String) {
        songRepository?.recalculateAndSetStorageUsage(userId)
    }

    fun sendFeedback(message: String, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                songRepository?.submitFeedback(message)
                onResult(true)
            } catch (e: Exception) {
                onResult(false)
            }
        }
    }
}

